/**
 * EOS in-progress list (M18.8) at /operations/eos.
 *
 * Lists Termination rows for the legal entity (?le=<uuid>) filtered
 * to non-terminal states by default. Click a row → navigate to the
 * EosDetailPage for the full settlement view + audit trace + exit
 * clearance checklist + gov filing drafts.
 */

import { useQuery } from "@tanstack/react-query";
import { LogOut, Plus, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  EmptyState,
  InlineAISuggestion,
  OperationsReportsButton,
  PageHeader,
  WorkflowStateBadge,
} from "../components/ui";
import { api } from "../lib/api";
import type { TerminationSummary } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

function fmtDate(d: string | null): string {
  if (!d) return "—";
  return d.slice(0, 10);
}

export function EosListPage() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();
  const params = new URLSearchParams(window.location.search);
  const state = params.get("state") ?? "";
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ["terminations", le, state],
    queryFn: () =>
      api.listTerminations({
        legalEntityId: le,
        state: state || undefined,
      }),
    enabled: !!le,
  });

  if (gate) return gate;
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  const rows: TerminationSummary[] = q.data ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("nav.eos")}
        subtitle={
          rows.length
            ? t("operations.eos_in_progress", { count: rows.length })
            : undefined
        }
        breadcrumbs={[
          { label: t("nav.operations"), href: "/operations" },
          { label: t("nav.eos") },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/operations/transactions/eos/new")}
              className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
            >
              <Plus className="h-4 w-4" />
              {t("operations.eos.cta.initiate", "Initiate Termination")}
            </button>
            <OperationsReportsButton
              domain="eos"
              domainLabel={t("nav.eos", "EOS")}
            />
          </div>
        }
      />

      {/* §4.6 — Rawatib EOS-modeling nudge */}
      <InlineAISuggestion
        agent="rawatib"
        icon={<Sparkles className="h-4 w-4" />}
        severity="info"
        headline={t(
          "operations.eos.suggestion.headline",
          "Model an EOS scenario before initiating.",
        )}
        body={t(
          "operations.eos.suggestion.body",
          "Rawatib: use EOS Calculation Drafts to compute settlement scenarios (resignation / dismissal / Article 80) before triggering the M18 termination workflow.",
        )}
        primaryAction={{
          label: t("operations.eos.suggestion.drafts_link", "Open Calculation Drafts"),
          onClick: () => navigate("/operations/eos/calculation-drafts"),
        }}
        suggestionKey="eos-list-drafts"
      />

      {rows.length === 0 ? (
        <EmptyState
          icon={LogOut}
          headline={t("operations.eos_empty_headline")}
          description={t("operations.eos_empty_body")}
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border-subtle bg-surface-default">
          <table className="w-full text-sm">
            <thead className="border-b border-border-subtle bg-surface-muted text-xs uppercase text-text-secondary">
              <tr>
                <th className="px-3 py-2 text-start">{t("operations.eos_col_termination")}</th>
                <th className="px-3 py-2 text-start">{t("operations.eos_col_employee")}</th>
                <th className="px-3 py-2 text-start">{t("operations.eos_col_type")}</th>
                <th className="px-3 py-2 text-start">{t("operations.eos_col_effective")}</th>
                <th className="px-3 py-2 text-start">{t("operations.eos_col_state")}</th>
                <th className="px-3 py-2 text-start">{t("operations.eos_col_initiated")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer border-b border-border-subtle hover:bg-surface-muted last:border-0"
                  onClick={() =>
                    navigate(`/operations/eos/${r.id}`)
                  }
                >
                  <td className="px-3 py-2 font-mono text-xs">
                    {/* M3X.UI-HYGIENE-PASS PR 3 — render employee_code
                        instead of a UUID prefix. The internal id stays
                        in the row key + onClick navigation. */}
                    {r.employee_code ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.employee_name_en ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className="rounded bg-surface-muted px-1.5 py-0.5">
                      {r.termination_type}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono">{r.effective_date}</td>
                  <td className="px-3 py-2">
                    <WorkflowStateBadge state={r.workflow_state} />
                  </td>
                  <td className="px-3 py-2 text-xs text-text-secondary">
                    {fmtDate(r.initiated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
